import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormInput,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateDietCategoryDetailsMutation } from '../../hooks'

const DietCategorySimpleDetailsForm = ({
  id,
  name,
  description,
  descriptionUrl,
  position,
  maxPosition,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DIET.UPDATE_SIMPLE_DETAILS,
  ])

  const [
    updateDietCategoryDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDietCategoryDetailsMutation()

  const initialValues = useMemo(
    () => ({
      description,
      position,
      descriptionUrl,
    }),
    [description, descriptionUrl, position],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateDietCategoryDetails(id, data),
    [id, updateDietCategoryDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Diet Category description error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [description, descriptionUrl, position, resetForm])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editDietCategory"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <FormInput
            disabled={!isFormEnabled}
            name="descriptionUrl"
            label="Description URL"
            labelAbove
            placeholder="Input description URL..."
            link={initialValues.descriptionUrl}
            rules={[
              {
                type: 'url',
                message: 'Please enter a valid URL!',
              },
            ]}
          />
        </Col>
      </Row>
      <Row gutter={[16]}>
        <Col span={12}>
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.DIET.UPDATE_SIMPLE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

DietCategorySimpleDetailsForm.propTypes = {
  id: idPropType.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  position: PropTypes.number.isRequired,
  maxPosition: PropTypes.number.isRequired,
  descriptionUrl: PropTypes.string,
}

DietCategorySimpleDetailsForm.defaultProps = {
  description: undefined,
  descriptionUrl: undefined,
}

export default DietCategorySimpleDetailsForm
